import { useEffect, useState } from 'react';
import { Breadcrumbs, Card, EmptyState } from '../../components';
import useDocumentHead from '../../hooks/useDocumentHead';
import { useLang } from '../../i18n';
import { get } from '../../services/api';
import './PartnersPage.scss';

interface Partner { id: string | number; name: string; logo?: string; description?: string; url?: string }

export default function PartnersPage() {
  const { t } = useLang();
  const [items, setItems] = useState<Partner[]>([]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await get<{ items?: Partner[] }>('/partners/');
        if (!cancel) setItems(res?.items || []);
      } catch {
        if (!cancel) setItems([]);
      }
    })();
    return () => { cancel = true; };
  }, []);

  useDocumentHead({
    title: `${t('partners.title')} — ${t('brand.name')}`,
    description: t('partners.sub'),
    canonical: 'https://ukam.uz/partners',
    og: { title: t('partners.title'), description: t('partners.sub'), type: 'website' },
    schemaOrg: { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('brand.name'), item: 'https://ukam.uz/' },
      { '@type': 'ListItem', position: 2, name: t('partners.title'), item: 'https://ukam.uz/partners' },
    ] },
  });

  return (
    <div className="partners-page">
      <div className="container partners-page__head">
        <Breadcrumbs items={[{ label: t('brand.name'), href: '/' }, { label: t('partners.title') }]} />
        <h1 className="partners-page__title">{t('partners.title')}</h1>
        <p className="partners-page__sub">{t('partners.sub')}</p>
      </div>
      <section className="container section">
        {items.length === 0 ? (
          <EmptyState title={t('services.empty')} />
        ) : (
          <div className="grid grid--3">
            {items.map((p) => {
              const card = (
                <Card variant="elevated" padding="md" className="partners-page__card">
                  {p.logo && <div className="partners-page__logo"><img src={p.logo} alt={p.name} /></div>}
                  <h3 className="partners-page__name">{p.name}</h3>
                  {p.description && <p className="partners-page__desc">{p.description}</p>}
                </Card>
              );
              return p.url ? (
                <a key={p.id} href={p.url} target="_blank" rel="noreferrer noopener" className="partners-page__link">{card}</a>
              ) : (
                <div key={p.id}>{card}</div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
